import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSavedFilterMutations, SAVED_FILTER_ERROR_KEYS } from "./useSavedFilterMutations";
import { useSavedFilterSharing, SHARING_ERROR_KEYS } from "./useSavedFilterSharing";
import { getAccessGrants, getFilterDetail } from "@/shared/infrastructure/adapters/filterAdapter";
import { cloneSharing, diffSharing, grantsToSharing, isPrivate, sharingToRequests, } from "@/shared/utils/filterSharing";
import { resolveApiErrorKey, resolveApiErrorMessage, } from "@/infrastructure/services/ApiErrorService";
import { invalidateFilters } from "./useFilters";
/**
 * Owns the whole save/edit/rename/delete/manage-access state machine for a list's
 * advanced-filter library. The three list views (contacts, organisations, usagers) differ
 * only in the `entity` they pass and the template they bind it to — every piece of state
 * and every handler lives here so the logic is never duplicated.
 */
export function useSavedFilterLifecycle(deps) {
    const { entity, application, draftAst, builderOpen, advancedFilter, tableQuery, onError, reloadList, onBuilderClosed, } = deps;
    const { t } = useI18n();
    const mutations = useSavedFilterMutations(entity, application);
    const sharingWriter = useSavedFilterSharing();
    /**
     * Non-null while the builder is editing an already-saved filter.
     *
     * Carries the name alongside the id — OPREF's PUT requires BOTH, and `onEditFilter` has
     * already fetched the detail, so the save path reads the name from here instead of issuing
     * a second GET for a value we hold. One ref rather than two so they cannot diverge (and so
     * no `?? ""` fallback can ever put an empty name on the wire).
     */
    const editingFilter = ref(null);
    const saveModalOpen = ref(false);
    const saveError = ref(null);
    const savePending = ref(false);
    const pendingAst = ref(null);
    const renameTarget = ref(null);
    const renameError = ref(null);
    const renamePending = ref(false);
    const deleteTarget = ref(null);
    const deleteModalOpen = ref(false);
    // Bumped on every onEditFilter call so a resolution that is no longer the latest
    // one in flight can recognize itself as stale and discard its result.
    let editRequestToken = 0;
    const manageTarget = ref(null);
    const manageSharing = ref(null);
    const manageKnownUsers = ref([]);
    const manageLoading = ref(false);
    const manageLoadError = ref(false);
    const managePending = ref(false);
    const manageError = ref(null);
    /** The server's grants the next access save is diffed against. */
    let manageBaseline = [];
    // Same role as editRequestToken, bumped on every open and close of the access modal.
    let manageRequestToken = 0;
    function onBuilderSave(ast) {
        const editing = editingFilter.value;
        if (editing) {
            void applyUpdate(editing, ast);
            return;
        }
        pendingAst.value = ast;
        saveError.value = null;
        saveModalOpen.value = true;
    }
    async function onSaveConfirmed(name, sharingInput) {
        if (!pendingAst.value)
            return;
        const sharing = cloneSharing(sharingInput);
        const requests = isPrivate(sharing) ? [] : sharingToRequests(sharing);
        const shared = requests.length > 0;
        savePending.value = true;
        saveError.value = null;
        try {
            // A shared save announces itself once, after the grants, rather than as "created".
            const created = await mutations.create({ name, criteria: pendingAst.value }, { announce: !shared && sharing.destination === "personal" });
            if (shared) {
                const { failures } = await sharingWriter.applyRequests(created.id, requests);
                invalidateFilters(entity);
                if (failures.length === 0) {
                    mutations.toastSuccess("advancedFilterLibrary.toast.shared", { name: created.name });
                }
                else {
                    // Say why when the refusal is a known one, e.g. OPUS down behind the grantee check.
                    const known = actionableSharingFailure(failures);
                    if (known) {
                        onError("advancedFilterLibrary.toast.shareIncompleteReason", {
                            name: created.name,
                            reason: resolveApiErrorMessage(known, SHARING_ERROR_KEYS, ""),
                        });
                    }
                    else {
                        onError("advancedFilterLibrary.toast.shareIncomplete", { name: created.name });
                    }
                }
            }
            else if (sharing.destination !== "personal") {
                mutations.toastSuccess("advancedFilterLibrary.toast.savedPrivate", { name: created.name });
            }
            saveModalOpen.value = false;
            closeBuilderAndSelect(created.id);
        }
        catch (e) {
            // Keep the modal open and show the failure under the name field.
            saveError.value = resolveApiErrorMessage(e, SAVED_FILTER_ERROR_KEYS, "advancedFilterLibrary.save.errors.generic");
        }
        finally {
            savePending.value = false;
        }
    }
    async function applyUpdate(editing, ast) {
        // Reuses `savePending` rather than a distinct flag: both this path (PUT) and
        // `onSaveConfirmed` (POST) are "a save mutation is in flight", the flag is read
        // only by the save-side UI (`SaveAdvancedFilterModal` and, with this change,
        // `AdvancedFilterBuilder` itself), and the two writers never run concurrently —
        // `onBuilderSave` dispatches to exactly one of them per call.
        savePending.value = true;
        try {
            await mutations.update(editing.id, editing.name, ast);
            closeBuilderAndSelect(editing.id);
        }
        catch (e) {
            // Map the backend code like the POST and rename paths do. OPREF's update handler
            // raises `SavedFilter.Name.Duplicate` (409) and `ForbiddenException` (403), and a
            // blanket generic toast would tell the user nothing they can act on (R70).
            // The KEY, not the message: `onError` is a toast helper that runs `t()` itself.
            onError(resolveApiErrorKey(e, SAVED_FILTER_ERROR_KEYS, "advancedFilterLibrary.save.errors.generic"));
        }
        finally {
            savePending.value = false;
        }
    }
    /** Close the builder and come back to the list with the filter selected. */
    function closeBuilderAndSelect(id) {
        // Capture what the search watcher observes BEFORE mutating anything below —
        // useSearch's watcher fires on changes to { activeViewId, page, pageSize, sort,
        // search, ast } (deep). Editing a filter that is already the active view (the
        // common case: library -> Modifier -> Enregistrer) leaves every one of those
        // unchanged: setView no-ops on the same id, page is already 1, and clear() is a
        // no-op because the library applies by filterId with no inline ast set. The
        // watcher then never fires and the grid keeps showing the previous criteria's
        // results, even though the write succeeded. Force the reload in exactly that case;
        // the create-new-filter path (activeViewId goes null -> id) and a stale page > 1
        // (setView resets it to 1) both already trigger the watcher on their own, so
        // forcing there too would double-fetch.
        const hadInlineAst = advancedFilter.appliedAst.value !== null;
        const sameView = tableQuery.state.activeViewId === id;
        const samePage = tableQuery.state.page === 1;
        builderOpen.value = false;
        editingFilter.value = null;
        pendingAst.value = null;
        advancedFilter.clear();
        tableQuery.setView(id);
        if (!hadInlineAst && sameView && samePage) {
            reloadList();
        }
    }
    async function onEditFilter(id) {
        const token = ++editRequestToken;
        let detail;
        try {
            detail = await getFilterDetail(id);
        }
        catch (e) {
            onError(resolveApiErrorKey(e, SAVED_FILTER_ERROR_KEYS, "advancedFilterLibrary.save.errors.generic"));
            return;
        }
        // Discard a stale resolution: either a newer edit request has started (this
        // one is no longer the latest), or the builder was already opened by another
        // path (e.g. "create a new filter") while this fetch was in flight. Applying
        // it now would set editingFilter/draftAst behind the user's back, turning
        // their next save into a silent PUT over the filter this fetch was for.
        if (token !== editRequestToken || builderOpen.value)
            return;
        editingFilter.value = { id, name: detail.name };
        draftAst.value = detail.criteria;
        builderOpen.value = true;
    }
    function onRenameFilter(id, name) {
        renameTarget.value = { id, name };
        // A previous attempt's failure must never greet the next rename.
        renameError.value = null;
    }
    async function onRenameConfirmed(name) {
        const target = renameTarget.value;
        if (!target)
            return;
        renamePending.value = true;
        try {
            await mutations.rename(target.id, name);
            renameTarget.value = null;
        }
        catch (e) {
            renameError.value = resolveApiErrorMessage(e, SAVED_FILTER_ERROR_KEYS, "advancedFilterLibrary.save.errors.generic");
        }
        finally {
            renamePending.value = false;
        }
    }
    function onDeleteFilter(id, name) {
        deleteTarget.value = { id, name };
        deleteModalOpen.value = true;
    }
    async function onManageAccess(id, name) {
        const token = ++manageRequestToken;
        manageTarget.value = { id, name };
        manageSharing.value = null;
        manageKnownUsers.value = [];
        manageLoading.value = true;
        manageLoadError.value = false;
        manageError.value = null;
        manageBaseline = [];
        let grants;
        try {
            grants = await getAccessGrants(id);
        }
        catch {
            if (token !== manageRequestToken)
                return;
            manageLoadError.value = true;
            manageLoading.value = false;
            return;
        }
        if (token !== manageRequestToken)
            return;
        manageBaseline = grants;
        const mapped = grantsToSharing(grants, t("advancedFilterLibrary.access.unknownUser"));
        manageSharing.value = mapped.sharing;
        manageKnownUsers.value = mapped.knownUsers;
        manageLoading.value = false;
    }
    async function onManageAccessConfirmed(sharingInput) {
        const target = manageTarget.value;
        if (!target)
            return;
        const sharing = cloneSharing(sharingInput);
        const requests = diffSharing(manageBaseline, sharing);
        if (requests.length === 0) {
            closeManageModal();
            return;
        }
        managePending.value = true;
        manageError.value = null;
        const { grants, failures } = await sharingWriter.applyRequests(target.id, requests);
        invalidateFilters(entity);
        managePending.value = false;
        if (failures.length === 0) {
            closeManageModal();
            mutations.toastSuccess("advancedFilterLibrary.toast.accessUpdated", { name: target.name });
            return;
        }
        // The edits stay on screen; only the diff baseline follows the server, so the next
        // save sends what is still missing.
        if (grants) {
            manageBaseline = grants;
            const known = grantsToSharing(grants, t("advancedFilterLibrary.access.unknownUser")).knownUsers;
            const knownIds = new Set(known.map((u) => u.id));
            manageKnownUsers.value = [...known, ...manageKnownUsers.value.filter((u) => !knownIds.has(u.id))];
        }
        manageSharing.value = sharing;
        manageError.value = resolveApiErrorMessage(actionableSharingFailure(failures) ?? failures[0], SHARING_ERROR_KEYS, "advancedFilterLibrary.access.manage.saveError");
    }
    /** One message for several failures: the one the owner can act on wins over a generic one. */
    function actionableSharingFailure(failures) {
        return failures.find((f) => resolveApiErrorKey(f, SHARING_ERROR_KEYS, "") !== "");
    }
    function closeManageModal() {
        // Stays open until the writes settle, so their outcome always lands in this modal.
        if (managePending.value)
            return;
        manageRequestToken++;
        manageTarget.value = null;
        manageSharing.value = null;
        manageKnownUsers.value = [];
        manageLoading.value = false;
        manageLoadError.value = false;
        managePending.value = false;
        manageError.value = null;
        manageBaseline = [];
    }
    /**
     * Visibility setters for the four modals.
     *
     * The state machine owns its own transitions: `SavedFilterLifecycleModals` receives this
     * object as a prop, so writing into the refs from its template would be a prop mutation
     * (`vue/no-mutating-props`). Handlers also keep the wiring declarative — a template binding
     * that reads `@update:model-value="closeSaveModal"` says what it does.
     */
    function setSaveModalOpen(open) {
        saveModalOpen.value = open;
    }
    function closeRenameModal() {
        // Nulling the target IS closing it — the modal's visibility derives from `renameTarget`.
        renameTarget.value = null;
    }
    function closeDeleteModal() {
        deleteModalOpen.value = false;
    }
    /**
     * The confirmation was dismissed (Cancel, backdrop, Escape) — drop the target.
     *
     * `ConfirmDeleteModal` emits `update:modelValue(false)` on BOTH exits and `cancel` only on
     * the dismissal, so the views bind visibility to the former and this to the latter. Without
     * it the abandoned target outlives its modal: `onDeleteConfirmed`'s `if (!target) return`
     * would stop guarding anything, and the (hidden) subtitle would keep interpolating the name
     * of a filter the user chose not to delete.
     *
     * Note `deleteTarget` cannot simply drive visibility the way `renameTarget` does: the modal
     * emits `update:modelValue(false)` BEFORE `confirm`, synchronously, so nulling the target
     * from the visibility handler would empty it before `onDeleteConfirmed` ever reads it.
     * That asymmetry with `renameTarget` is deliberate, not an oversight.
     */
    function onDeleteCancelled() {
        deleteTarget.value = null;
    }
    async function onDeleteConfirmed() {
        const target = deleteTarget.value;
        if (!target)
            return;
        try {
            await mutations.remove(target.id, target.name);
            if (tableQuery.state.activeViewId === target.id)
                tableQuery.setView(null);
        }
        catch (e) {
            onError(resolveApiErrorKey(e, SAVED_FILTER_ERROR_KEYS, "advancedFilterLibrary.toast.deleteError"));
        }
        finally {
            deleteTarget.value = null;
            deleteModalOpen.value = false;
        }
    }
    /** The builder must leave edit mode whenever it closes for any other reason. */
    function closeBuilder() {
        builderOpen.value = false;
        editingFilter.value = null;
        onBuilderClosed?.();
    }
    return {
        editingFilter,
        saveModalOpen,
        saveError,
        savePending,
        renameTarget,
        renameError,
        renamePending,
        deleteTarget,
        deleteModalOpen,
        onBuilderSave,
        onSaveConfirmed,
        setSaveModalOpen,
        closeRenameModal,
        closeDeleteModal,
        closeBuilder,
        onEditFilter,
        onRenameFilter,
        onRenameConfirmed,
        onDeleteFilter,
        onDeleteCancelled,
        onDeleteConfirmed,
        manageTarget,
        manageSharing,
        manageKnownUsers,
        manageLoading,
        manageLoadError,
        managePending,
        manageError,
        onManageAccess,
        onManageAccessConfirmed,
        closeManageModal,
    };
}
